import {
  Card,
  SkeletonBlock,
  Typography,
} from '@one-thd/sui-atomic-components';
import React, { useEffect, useState } from 'react';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { BuyItAgainAPI } from '../../../api/oms/order-history';
import {
  useCommonContext,
  useExperienceContext,
} from '../../../hooks/useContexts';
import { IBuyItAgainRequest } from '../../../models/purchase-history';
import { IBuyAgainItem } from '../../../models/purchase-history/buy-it-again';
import { pushEventInfo } from '../../../utils/analytics';
import { getWindow } from '../../../utils/general';
import { getSvocId } from '../../../utils/user';
import { DateRanges } from '../../../utils/DateRanges';

export const BuyItAgain = () => {
  const [buyAgainItems, setBuyAgainItems] = useState<IBuyAgainItem[]>([]);
  const [showError, setShowError] = useState(false);
  const [noBuyItAgainItems, setNoBuyItAgainItems] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { userID } = useThdCustomer();
  const { isDesktop, timezone } = useCommonContext();
  const { isConsumerApp } = useExperienceContext();
  const itemLimit = isDesktop ? 4 : 8;

  const getBuyItAgainData = async () => {
    try {
      const request: IBuyItAgainRequest = {
        customerAccountId: getSvocId() || '',
        startDate: DateRanges.last2Years.startDate,
        endDate: DateRanges.last2Years.endDate,
        offset: '8',
        timezone,
      };
      const response = await BuyItAgainAPI(request, userID);
      if (response.data) {
        setBuyAgainItems(response?.data?.buyAgainItems);
        if (response?.data?.buyAgainItems.length === 0) {
          setNoBuyItAgainItems(true);
        }
      }
    } catch (error) {
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isConsumerApp && buyAgainItems.length === 0 && !showError) {
      getBuyItAgainData();
    }
  }, []);

  if (isConsumerApp || noBuyItAgainItems) return null;

  if (isLoading) {
    return (
      <Card href="#" data-testid="buyItAgain" className="lg:sui-w-1/2">
        <div className="sui-flex sui-flex-row sui-justify-between sui-w-full">
          <Typography weight="bold" variant="h5">
            Buy It Again
          </Typography>
          <Typography> View All </Typography>
        </div>
        <div className="sui-flex sui-gap-3">
          {[...Array(itemLimit)].map((_e, i) => (
            <div className="sui-mr-9" key={i}>
              <SkeletonBlock height={14} width={14} />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (showError) {
    return (
      <Card href="#" data-testid="buyItAgain" className="lg:sui-w-1/2">
        <div className="sui-flex sui-flex-row sui-justify-between sui-w-full">
          <Typography weight="bold" variant="h5">
            Buy It Again
          </Typography>
          <Typography> View All </Typography>
        </div>
        <div className="sui-flex sui-gap-3">
          <Typography>
            We were unable to retrieve your Buy It Again details. Refresh the
            page and try again.
          </Typography>
        </div>
      </Card>
    );
  }

  if (buyAgainItems.length > 0) {
    return (
      <Card
        href={`https://${getWindow()?.location.hostname}/buyitagain`}
        className="lg:sui-w-1/2"
        data-testid="buyItAgain"
        onClick={() => {
          pushEventInfo({
            category: { primaryCategory: 'button submission' },
            eventInfo: { eventName: 'my account settings' },
            myAccount: { settings: 'purchase history: buy it again container' },
          });
        }}
      >
        <div className="sui-flex sui-justify-between">
          <Typography style={{ fontWeight: 'bold' }} variant="h5">
            Buy It Again
          </Typography>
          <Typography> View All </Typography>
        </div>

        <div className="sui-flex sui-gap-14 sui-max-h-16">
          {buyAgainItems.map(
            (
              el: { itemDescFromCatalog: string; buyItAgainImage: string },
              i: number,
            ) => {
              if (i < itemLimit) {
                return el?.buyItAgainImage ? (
                  <img
                    width="60px"
                    height="60px"
                    src={el.buyItAgainImage}
                    alt={el.itemDescFromCatalog}
                    key={i}
                  />
                ) : (
                  <div key={i}>
                    <img
                      src="https://assets.thdstatic.com/images/v1/no-img.svg"
                      alt="Unavailable"
                      width="60px"
                      height="60px"
                    />
                  </div>
                );
              }
              return null;
            },
          )}
        </div>
      </Card>
    );
  }

  return null;
};
